import { useState, useEffect, useLayoutEffect } from 'react';
import { HUD_COMPACT_LAYOUT } from '../../../config/ui/hudDefaults.config';

/**
 * HUD Top Layout Coordination — U2 (2026-09-28).
 *
 * Two INDEPENDENT top HUD anchors, deliberately no longer modeled as one
 * centered stack:
 *
 * - Map Banner (#hud-map-banner): stays top-center. Its measured bottom is the
 *   reference the frozen U01 harness uses for the target frame's separation.
 * - TargetInfoPanel (selected target frame, HP bar, status chips): anchors in
 *   the LEFT HUD column, aligned under the docked player unit frame
 *   (#player-info) when that element is measurable (same measured-element
 *   pattern as the banner, with a banner-clearance fallback for jsdom / when
 *   the unit frame is absent).
 *
 * Why: DJ's U01 capture review read the previous centered placement as a
 * second banner row ("sits directly under the map banner ... like a banner
 * stack"). A target frame docked beside the player frame reads as a unit
 * frame, not as banner chrome. The right-aligned option was rejected — the
 * top-right stack already owns the minimap/quest tracker, so proving
 * non-collision there would depend on those surfaces' growth.
 *
 * Frozen U01 desktop/mobile contract preserved in source (the harness itself
 * is read-only evidence and must stay untouched):
 *   - target.top >= banner.bottom + TARGET_PANEL_MIN_CLEARANCE (8 px; the
 *     harness constant). ELEMENT_GAP (10 px) >= 8 px, and the left-column
 *     anchor can only push the frame further DOWN, never up.
 *   - target + banner horizontally contained, target vertically contained:
 *     targetPanelMaxWidth is clamped to the viewport (left offset included),
 *     and a compact-height clamp + targetPanelMaxHeight keeps the frame inside
 *     short viewports (the component applies the scrolling/compact treatment).
 */

export const HUD_TOP_CONSTANTS = {
  BANNER_DEFAULT_TOP: 10,
  ELEMENT_GAP: 10,
  TARGET_PANEL_STANDALONE_TOP: 16,
  /** Left x of the docked unit-frame column (matches #player-info's inset). */
  TARGET_PANEL_LEFT: 16,
  /**
   * The frozen U01 harness requires target.top >= banner.bottom + 8. The
   * layout never places the frame closer than ELEMENT_GAP (10), so this is an
   * explicit floor for reviewers: 10 >= 8 holds by construction.
   */
  TARGET_PANEL_MIN_CLEARANCE: 8,
  PANEL_MAX_WIDTH: 300,
  PANEL_MIN_WIDTH: 250,
  /** Minimum panel body kept inside the viewport before the compact clamp. */
  PANEL_MIN_VISIBLE_HEIGHT: 120,
  VIEWPORT_MARGIN: 16,
  /** Short-viewport breakpoint — single source: HUD_COMPACT_LAYOUT.maxViewportHeight. */
  COMPACT_HEIGHT_THRESHOLD: HUD_COMPACT_LAYOUT.maxViewportHeight,
} as const;

export interface HudTopLayoutOptions {
  viewportWidth?: number;
  viewportHeight?: number;
  bannerBottom?: number | null;
  hasBanner?: boolean;
  hasDebugText?: boolean;
  /** Measured bottom of the docked player unit frame (#player-info), or null. */
  playerInfoBottom?: number | null;
  /** Measured left of the docked player unit frame (#player-info), or null. */
  playerInfoLeft?: number | null;
}

export interface HudTopLayoutResult {
  bannerTop: number;
  /**
   * Top edge of the band the target frame may occupy (banner bottom + the
   * element gap; the standalone inset when the banner is absent/unmeasured).
   * Shared with the compact landscape HUD, which anchors the quick menu below
   * the same clearance (hudCompactLayout.ts).
   */
  bannerClearanceTop: number;
  targetPanelTop: number;
  targetPanelLeft: number;
  /** Viewport-derived cap the panel applies as maxHeight (compact/scroll treatment). */
  targetPanelMaxHeight: number;
  bannerMaxWidth: number;
  targetPanelMaxWidth: number;
  targetPanelMinWidth: number;
  isCompactHeight: boolean;
}

/** Finite positive number or null — guards NaN/0 rects from jsdom or hidden DOM. */
function measuredPositive(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Top edge of the band below the map banner that top-anchored HUD surfaces may
 * occupy. Uses the LIVE measured banner bottom when available, otherwise the
 * standalone inset (no banner) or the documented banner estimate (banner
 * mounted but not yet measurable — jsdom, first paint, hidden DOM).
 */
export function computeBannerClearanceTop(
  options: Pick<HudTopLayoutOptions, 'hasBanner' | 'bannerBottom' | 'hasDebugText'> = {},
): number {
  const measuredBannerBottom = measuredPositive(options.bannerBottom);
  if (!options.hasBanner && measuredBannerBottom == null) {
    // Standalone panel when banner is absent
    return HUD_TOP_CONSTANTS.TARGET_PANEL_STANDALONE_TOP;
  }
  if (measuredBannerBottom != null) {
    // Measured live bottom of the banner
    return Math.round(measuredBannerBottom + HUD_TOP_CONSTANTS.ELEMENT_GAP);
  }
  // Fallback estimate when banner is present but DOM measurement is 0 (e.g. jsdom tests)
  // Base banner: top 10 + 8px padding*2 + 18px text + 2px border = ~46px bottom
  // With debug text: + 2px gap + 14px mono text = ~62px bottom
  const estimatedBannerHeight = options.hasDebugText ? 54 : 38;
  return HUD_TOP_CONSTANTS.BANNER_DEFAULT_TOP + estimatedBannerHeight + HUD_TOP_CONSTANTS.ELEMENT_GAP;
}

/**
 * Compute the top coordinate and horizontal constraints for the target panel
 * and map banner.
 */
export function computeHudTopLayout(options: HudTopLayoutOptions = {}): HudTopLayoutResult {
  const width = options.viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1440);
  const height = options.viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 1000);
  const isCompactHeight = height <= HUD_TOP_CONSTANTS.COMPACT_HEIGHT_THRESHOLD;

  const bannerTop = HUD_TOP_CONSTANTS.BANNER_DEFAULT_TOP;
  const bannerMaxWidth = Math.max(180, width - HUD_TOP_CONSTANTS.VIEWPORT_MARGIN * 2);

  // 1. Banner clearance (top edge of the frame's allowed band).
  const bannerClearanceTop = computeBannerClearanceTop(options);

  // 2. Left-column anchor — below the measured unit frame when deeper than the
  //    banner clearance. Never ABOVE the banner clearance (max, not replace).
  const measuredPlayerInfoBottom = measuredPositive(options.playerInfoBottom);
  const playerInfoAnchorTop = measuredPlayerInfoBottom != null
    ? Math.round(measuredPlayerInfoBottom + HUD_TOP_CONSTANTS.ELEMENT_GAP)
    : null;
  const unboundedTop = playerInfoAnchorTop != null
    ? Math.max(bannerClearanceTop, playerInfoAnchorTop)
    : bannerClearanceTop;

  // 3. Vertical containment: on short viewports keep at least the minimum
  //    readable body inside the viewport. The banner clearance is the floor —
  //    if the viewport is too short for both, the top contract wins and the
  //    component's maxHeight/scroll treatment bounds the rest.
  const maxContainedTop = Math.max(
    bannerClearanceTop,
    Math.round(height - HUD_TOP_CONSTANTS.VIEWPORT_MARGIN - HUD_TOP_CONSTANTS.PANEL_MIN_VISIBLE_HEIGHT),
  );
  const targetPanelTop = Math.min(unboundedTop, maxContainedTop);
  const targetPanelMaxHeight = Math.max(
    HUD_TOP_CONSTANTS.PANEL_MIN_VISIBLE_HEIGHT,
    Math.round(height - targetPanelTop - HUD_TOP_CONSTANTS.VIEWPORT_MARGIN),
  );

  // 4. Horizontal constraints: responsive width, left column, right-edge
  //    containment (the left offset shifts toward 0 only in sub-panel viewports).
  const targetPanelMaxWidth = Math.min(
    HUD_TOP_CONSTANTS.PANEL_MAX_WIDTH,
    Math.max(180, width - HUD_TOP_CONSTANTS.VIEWPORT_MARGIN * 2),
  );
  const targetPanelMinWidth = Math.min(HUD_TOP_CONSTANTS.PANEL_MIN_WIDTH, targetPanelMaxWidth);
  // A measured left of 0 is legitimate (unlike the bottoms, which must be > 0).
  const measuredPlayerInfoLeft = options.playerInfoLeft != null
    && Number.isFinite(options.playerInfoLeft)
    && options.playerInfoLeft >= 0
    ? Math.round(options.playerInfoLeft)
    : null;
  const preferredLeft = measuredPlayerInfoLeft ?? HUD_TOP_CONSTANTS.TARGET_PANEL_LEFT;
  const targetPanelLeft = Math.min(
    Math.max(0, preferredLeft),
    Math.max(0, Math.round(width - targetPanelMaxWidth)),
  );

  return {
    bannerTop,
    bannerClearanceTop,
    targetPanelTop,
    targetPanelLeft,
    targetPanelMaxHeight,
    bannerMaxWidth,
    targetPanelMaxWidth,
    targetPanelMinWidth,
    isCompactHeight,
  };
}

/** One live DOM element the hook re-measures on resize/mutation. */
interface TrackedTopElement {
  element: Element;
  resizeObserver: ResizeObserver | null;
  mutationObserver: MutationObserver | null;
}

type TrackedKey = 'banner' | 'player-info';

/**
 * React hook for responsive top HUD layout in TargetInfoPanel and related components.
 * Bounded subscription lifecycle (U2 added #player-info to the same pattern):
 * - Targeted ResizeObserver per measured element (#hud-map-banner, #player-info)
 * - Targeted MutationObserver per measured element (characterData, subtree, attributes)
 * - Container-level childList observer for mount/unmount detection
 * - Font settlement (document.fonts.ready)
 * - Window resize listener
 * - Clean disposal of all observers and listeners
 */
export function useHudTopLayout(hasBanner = true, hasDebugText = false): HudTopLayoutResult {
  const [dimensions, setDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1440,
    height: typeof window !== 'undefined' ? window.innerHeight : 1000,
  }));

  const [measuredBannerBottom, setMeasuredBannerBottom] = useState<number | null>(null);
  const [measuredPlayerInfo, setMeasuredPlayerInfo] = useState<{ bottom: number; left: number } | null>(null);

  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    let isDisposed = false;
    let containerObserver: MutationObserver | null = null;
    const tracked = new Map<TrackedKey, TrackedTopElement>();

    const releaseTracked = (key: TrackedKey): void => {
      const entry = tracked.get(key);
      if (!entry) return;
      if (entry.resizeObserver) entry.resizeObserver.disconnect();
      if (entry.mutationObserver) entry.mutationObserver.disconnect();
      tracked.delete(key);
    };

    const trackElement = (key: TrackedKey, element: Element): void => {
      const existing = tracked.get(key);
      if (existing && existing.element === element) return;
      releaseTracked(key);

      const entry: TrackedTopElement = { element, resizeObserver: null, mutationObserver: null };
      if (typeof ResizeObserver !== 'undefined') {
        entry.resizeObserver = new ResizeObserver(() => {
          if (!isDisposed) measureAll();
        });
        entry.resizeObserver.observe(element);
      }
      // The element's OWN attributes only (style/class can move it without
      // resizing it). Not its subtree: #player-info's vitals labels and bars
      // change many times a second in a fight, and each callback here reads
      // getBoundingClientRect, which forces a synchronous layout of the whole
      // page (lane AL, 2026-10-08: ~14 ms/s of forced layout in a First Field
      // fight). A content change that resizes the element reaches the
      // ResizeObserver above, which runs after layout and costs nothing extra.
      if (typeof MutationObserver !== 'undefined') {
        entry.mutationObserver = new MutationObserver(() => {
          if (!isDisposed) measureAll();
        });
        entry.mutationObserver.observe(element, { attributes: true });
      }
      tracked.set(key, entry);
    };

    const measureBanner = (): void => {
      if (isDisposed) return;
      const bannerEl = document.getElementById('hud-map-banner') || document.querySelector('[data-testid="map-banner"]');
      if (!bannerEl) {
        releaseTracked('banner');
        setMeasuredBannerBottom(null);
        return;
      }
      const rect = bannerEl.getBoundingClientRect();
      setMeasuredBannerBottom(rect.height > 0 && rect.bottom > 0 ? rect.bottom : null);
      trackElement('banner', bannerEl);
    };

    /**
     * #player-info is the docked unit frame's positioning container (HUD.tsx).
     * Its measured rect gives the left-column anchor. Missing element (jsdom,
     * collapsed HUD, panel disabled) → null → banner-clearance fallback.
     */
    const measurePlayerInfo = (): void => {
      if (isDisposed) return;
      const playerInfoEl = document.getElementById('player-info');
      if (!playerInfoEl) {
        releaseTracked('player-info');
        setMeasuredPlayerInfo(null);
        return;
      }
      const rect = playerInfoEl.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0) {
        setMeasuredPlayerInfo(previous => (
          previous && previous.bottom === rect.bottom && previous.left === rect.left
            ? previous
            : { bottom: rect.bottom, left: rect.left }
        ));
      } else {
        setMeasuredPlayerInfo(null);
      }
      trackElement('player-info', playerInfoEl);
    };

    const measureAll = (): void => {
      measureBanner();
      measurePlayerInfo();
    };

    // Initial measurement
    measureAll();

    // Font settlement: re-measure when web fonts settle
    if ('fonts' in document && (document.fonts as any)?.ready) {
      (document.fonts as any).ready
        .then(() => {
          if (!isDisposed) measureAll();
        })
        .catch(() => { /* ignore font loading errors */ });
    }

    const handleResize = () => {
      if (isDisposed) return;
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      measureAll();
    };

    window.addEventListener('resize', handleResize);

    // Watch container only for element mount / unmount (bounded childList
    // observation). Any node added or removed anywhere under #root lands here
    // (chat lines, tooltips, cooldown labels), so only re-measure when a
    // tracked element actually appeared, disappeared or was replaced — an
    // id lookup, no layout read.
    const trackedChanged = (): boolean => {
      const banner = document.getElementById('hud-map-banner') || document.querySelector('[data-testid="map-banner"]');
      const playerInfo = document.getElementById('player-info');
      return (tracked.get('banner')?.element ?? null) !== banner
        || (tracked.get('player-info')?.element ?? null) !== playerInfo;
    };
    if (typeof MutationObserver !== 'undefined') {
      const container = document.getElementById('root') || document.body;
      containerObserver = new MutationObserver(() => {
        if (!isDisposed && trackedChanged()) measureAll();
      });
      containerObserver.observe(container, { childList: true, subtree: true });
    }

    return () => {
      isDisposed = true;
      window.removeEventListener('resize', handleResize);
      for (const key of Array.from(tracked.keys())) releaseTracked(key);
      if (containerObserver) {
        containerObserver.disconnect();
        containerObserver = null;
      }
    };
  }, []);

  return computeHudTopLayout({
    viewportWidth: dimensions.width,
    viewportHeight: dimensions.height,
    bannerBottom: measuredBannerBottom,
    hasBanner,
    hasDebugText,
    playerInfoBottom: measuredPlayerInfo?.bottom ?? null,
    playerInfoLeft: measuredPlayerInfo?.left ?? null,
  });
}
